import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link2, Wand2 } from "lucide-react";
import { vzLinkStore, vzApplyMatches } from "@/lib/verizonSalesApi";
import { getLocations } from "@/lib/api";
import { OwnershipChanges } from "@/components/verizon/OwnershipChanges";

export const StoreMatching = ({ stores, onChanged }) => {
  const [locations, setLocations] = useState([]);
  const [picks, setPicks] = useState({});
  useEffect(() => {
    getLocations(false).then((d) => setLocations(d || [])).catch(() => setLocations([]));
  }, []);

  const unmatched = stores.filter((s) => !s.location);
  const addressMatched = stores.filter((s) => s.location?.match === "address").length;

  const link = async (s) => {
    try { await vzLinkStore(s.door_tsp, picks[s.door_tsp]); toast.success(`Linked ${s.store_id} (TSP ${s.door_tsp})`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
  };
  const apply = async () => {
    try { const r = await vzApplyMatches(); toast.success(`Saved TSP IDs on ${r.updated} locations`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to save matches"); }
  };

  return (
    <div className="space-y-4">
    <OwnershipChanges stores={stores} locations={locations} onChanged={onChanged} />
    <Card className="border-slate-200" data-testid="vz-store-matching">
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4 text-teal" /> Store ↔ Location matching</CardTitle>
        {addressMatched > 0 && (
          <Button size="sm" variant="outline" onClick={apply} data-testid="vz-apply-matches-btn">
            <Wand2 className="w-4 h-4 mr-1" /> Save TSP IDs for {addressMatched} address matches
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-3">
          Stores are matched to portal Locations by <b>TSP ID</b>, then by street number + ZIP. Dealers only see stores linked to their assigned locations (or with the same Sub-Agent ID).
          {" "}<b>{unmatched.length}</b> Verizon stores are not linked yet.
        </p>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {unmatched.map((s) => (
            <div key={s.door_tsp} className="flex items-center gap-2 flex-wrap border border-slate-100 rounded-lg p-2 text-xs" data-testid={`vz-unmatched-${s.door_tsp}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium text-navy">{s.store_id} · TSP {s.door_tsp} · {s.sub_agent_name}</div>
                <div className="text-slate-500">{s.address}, {s.city}, {s.state} {s.zip_code}</div>
              </div>
              <Select value={picks[s.door_tsp] || ""} onValueChange={(v) => setPicks({ ...picks, [s.door_tsp]: v })}>
                <SelectTrigger className="h-8 w-64 text-xs" data-testid={`vz-link-select-${s.door_tsp}`}><SelectValue placeholder="Choose portal location…" /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {l.city}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!picks[s.door_tsp]} onClick={() => link(s)} data-testid={`vz-link-btn-${s.door_tsp}`}>Link</Button>
            </div>
          ))}
          {unmatched.length === 0 && <p className="text-sm text-emerald-600">All Verizon stores are linked to portal locations.</p>}
        </div>
      </CardContent>
    </Card>
    </div>
  );
};
